
import { BlogPost } from "./BlogPost";
import { BlogParagraph, BlogHeading, BlogEmphasis, BlogContent, BlogQuizQuestion } from "./BlogContent";
import { ReactNode } from "react";
import { ExternalLink } from "lucide-react";

interface BlogPostsListProps {
  expandedPosts: {[key: string]: boolean};
  togglePost: (postId: string) => void;
}

export const BlogPostsList = ({ expandedPosts, togglePost }: BlogPostsListProps) => {
  return (
    <div className="prose max-w-none">
      {/* First row: 2 columns layout with posts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* First blog post */}
        <BlogPost
          id="post1"
          title="Lancement de l'association !"
          imageSrc="/lovable-uploads/606d5e71-e641-4a24-87d6-71a69db64622.png"
          imageAlt="Lancement de l'association"
          isExpanded={expandedPosts.post1}
          onToggle={togglePost}
          content={
            <BlogParagraph>
              Le 3 avril dernier, nous avons franchi une étape décisive : le lancement officiel de l'association Enfants de la République. Cette soirée, organisée dans un lieu chargé de sens, a réuni nos proches, partenaires et premiers soutiens. Elle a été l'aboutissement de mois de préparation, de réflexions collectives et d'engagements passionnés.
            </BlogParagraph>
          }
        />
        
        {/* Second blog post */}
        <BlogPost
          id="post2"
          title="Pourquoi avoir fondé Enfants de la République ?"
          imageSrc="/lovable-uploads/0710a922-6851-406e-a535-3236a6fcf8ec.png"
          imageAlt="Frédéric Sebag fondateur"
          externalLink="https://www.linkedin.com/posts/frederic-sebag-open_raezpublique-engagementcitoyen-charteraezpublicaine-ugcPost-7323298401696030721-7lu4?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAAGm-kBefq8ZDR6kzoOJo0LuBNMuuNqvwc"
          isExpanded={expandedPosts.post2}
          onToggle={togglePost}
          content={
            <>
              <BlogParagraph>
                Pourquoi ai-je fondé Enfants de la République ? Parce que je crois profondément que la citoyenneté ne va plus de soi. Il est urgent de redonner du sens à ce qui nous unit.
              </BlogParagraph>
              <BlogParagraph>
                Mon point de départ avec quelques amis : la Charte des Droits et Devoirs du Citoyen Français (article 21-24 du code civil). Un texte trop méconnu, pourtant essentiel. 
                Et si on en faisait un repère commun ? Un pacte à partager ?
              </BlogParagraph>
              <BlogParagraph>
                Transmettre, Renforcer, Protéger cette charte, trois missions pour faire vivre concrètement les valeurs de la #République et notre citoyenneté.
              </BlogParagraph>
              <BlogParagraph>
                Je vous en parle en vidéo. 
                <a 
                  href="https://www.linkedin.com/posts/frederic-sebag-open_raezpublique-engagementcitoyen-charteraezpublicaine-ugcPost-7323298401696030721-7lu4?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAAGm-kBefq8ZDR6kzoOJo0LuBNMuuNqvwc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center ml-2"
                >
                  Regarder la vidéo sur LinkedIn <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </BlogParagraph>
              <BlogEmphasis>
                Faisons vivre ensemble ce que nous avons de plus précieux !
              </BlogEmphasis>
              <p className="text-gray-600">
                #EngagementCitoyen #CharteRépublicaine #Transmission #VivreEnsemble 
                #EducationCivique #Laïcité #EnfantsDeLaRépublique #ProjetCollectif #Lancement
              </p>
            </>
          }
        />
      </div>

      {/* Second row: third post and fourth post */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Third blog post */}
        <BlogPost
          id="post3"
          title="Mon engagement"
          imageSrc="/lovable-uploads/4ea78e2f-a0a3-40c7-bcc9-20d2d7b51ff1.png"
          imageAlt="Gérard Pazuelo - Membre de l'Association"
          externalLink="https://www.linkedin.com/posts/gerard-pazuelo-7a118a_engagementcitoyen-charteraezpublicaine-transmission-activity-7327334112099201024-sIfK?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAAGm-kBefq8ZDR6kzoOJo0LuBNMuuNqvwc"
          isExpanded={expandedPosts.post3}
          onToggle={togglePost}
          content={
            <>
              <BlogParagraph>
                Pour moi, @Enfant de la République, c'est un cri, un cri d'alerte sur ce que nous avons trop oublié : nos devoirs.
              </BlogParagraph>
              <BlogParagraph>
                Vivre en République, c'est une chance et aussi une responsabilité collective. Nous ne pourrons construire un avenir commun que si chacun d'entre nous, quelles que soient ses origines, son parcours, son métier, réactive cette conscience citoyenne.
              </BlogParagraph>
              <BlogParagraph>
                C'est pour cela que j'ai décidé de m'engager auprès de Enfants de la République : parce que cette association remet au centre du débat un texte trop peu connu et pourtant fondamental – la Charte des droits et des devoirs du citoyen français (art. 21-24 du Code civil).
              </BlogParagraph>
              <BlogParagraph>
                Les mots ne suffisent plus. Place à l'action. À la transmission. À la mobilisation.
              </BlogParagraph>
              <BlogParagraph>
                <a 
                  href="https://www.linkedin.com/posts/gerard-pazuelo-7a118a_engagementcitoyen-charteraezpublicaine-transmission-activity-7327334112099201024-sIfK?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAAGm-kBefq8ZDR6kzoOJo0LuBNMuuNqvwc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center"
                >
                  Lire le post complet sur LinkedIn <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </BlogParagraph>
            </>
          }
        />
        
        {/* Fourth blog post */}
        <BlogPost
          id="post4"
          title="L'association en actions"
          imageSrc="/lovable-uploads/63b93242-4d59-4c78-b920-11129c956563.png"
          imageAlt="Liberté, égalité, fraternité & laïcité"
          isExpanded={expandedPosts.post4}
          onToggle={togglePost}
          content={
            <>
              <BlogHeading>Le lancement !</BlogHeading>
              <BlogParagraph>
                Le 3 avril dernier, nous avons effectué le lancement officiel de l'association Les Enfants de la République. Cette soirée a réuni les cent personnes auprès de qui nous voulions tester nos idées et nos propositions. Elle a été l'aboutissement de 10 mois de préparation, de réflexions collectives et d'engagements passionnés.
              </BlogParagraph>
              <BlogParagraph>
                Nos invités, enseignants, juristes, anciens élus, jeunes professionnels, retraités engagés, … sont venus partager un même besoin de dialoguer, d'agir face aux défis de notre temps : ceux du repli communautaire, de l'effritement du civisme ou de l'érosion des fondamentaux républicains.
              </BlogParagraph>
              <BlogParagraph>
                Cette soirée ne fut pas qu'un test. Elle a marqué l'entrée en action d'un collectif décidé à renforcer notre esprit républicain par un travail patient, structuré et ambitieux. Trois missions guident notre démarche : Transmettre, Renforcer, Protéger en utilisant La Charte des Droits et Devoirs du citoyen français comme axe central. Nous avons exposé notre démarche, son fondement, son objectif, sa méthode et les premières actions que nous engageons.
              </BlogParagraph>
              <BlogParagraph>
                Une soirée passionnante qui a permis aussi de recueillir les premiers adhérents ainsi que les premiers dons nécessaires au développement de nos projets.
              </BlogParagraph>
              <BlogEmphasis>Merci à vous !</BlogEmphasis>
              
              <BlogHeading>Le focus des premières semaines :</BlogHeading>
              <BlogParagraph>
                Notre première étape clé ? Faire découvrir la Charte au plus grand nombre afin de permettre à chacun de comprendre l'indispensabilité de ce qui constitue notre pacte du vivre ensemble. Nous devons, dans la foulée, amener chacun à se réengager dans sa citoyenneté en la signant, en l'adoptant.
              </BlogParagraph>
              <BlogParagraph>
                Pour cela, nous lançons nos actions de visibilité de l'Association sur les Réseaux sociaux, principalement LinkedIn et Instagram afin de nous faire connaitre et déclencher les premières prises de conscience, favoriser les adhésions et l'appropriation de notre démarche.
              </BlogParagraph>
              <BlogParagraph>
                Nous préparons également les modules pédagogiques pour découvrir et comprendre la Charte. Ces deux démarches seront relayées dès la rentrée par des actions publiques dans la rue, dans des collèges et dans certains lieux publics.
              </BlogParagraph>
              <BlogParagraph>
                Ce plan de communication doit donner un maximum de visibilité à notre association, à la Charte et doit permettre aussi de fédérer ceux qui veulent signer et adhérer.
              </BlogParagraph>
              <BlogEmphasis>Objectif : 100 000 signatures à la fin de l'année et une Charte qui redevient un objet de fierté collective.</BlogEmphasis>
              
              <BlogHeading>Le Concours « Exprime la Charte » :</BlogHeading>
              <BlogParagraph>
                La citoyenneté ne s'apprend pas du jour au lendemain, et certainement pas uniquement à l'âge adulte. C'est dès l'enfance et tout au long de l'adolescence, qu'il est essentiel d'aider les jeunes à comprendre le monde qui les entoure, à développer leur esprit critique constructif, et à prendre conscience de leurs droits - mais aussi de leurs devoirs - en tant que citoyens. Cet éveil progressif construit les bases d'un vivre-ensemble apaisé et prépare des individus responsables, capables de s'engager dans la vie républicaine.
              </BlogParagraph>
              <BlogParagraph>
                Entre 13 et 16 ans, cet apprentissage prend une dimension particulière. A cet âge charnière, les adolescents affirment leur personnalité, questionnent les normes, et explorent de nouvelles idées. C'est une période précieuse pour leur permettre de s'exprimer, de réfléchir aux enjeux sociaux, culturels et environnementaux et de s'impliquer, à leur manière, dans la construction du monde de demain.
              </BlogParagraph>
              <BlogParagraph>
                C'est pourquoi Enfants de la République va lancer dans les prochaines semaines le concours "Exprime la charte !" qui leur est destiné aux jeunes de 13 à 16 ans. Réinterpréter la charte des Droits et Devoirs du citoyen avec leurs propres codes (en mots, en images ou en musique), c'est l'occasion unique pour ces jeunes de faire entendre leur voix, de valoriser leur créativité et de réfléchir ensemble aux valeurs communes qui fondent notre République.
              </BlogParagraph>
              <BlogParagraph>
                Pour nous accompagner dans cette démarche, nous avons décidé de créer un Conseil des Jeunes Sages composé de 8 jeunes aux profils aussi variés que possible. Leur mission : participer à la conception du concours, contribuer à l'élaboration de ses règles et à leur bonne communication, et prendre part au choix des lauréats.
              </BlogParagraph>
              <BlogParagraph>
                En leur confiant une telle responsabilité, nous affirmons notre volonté de co-construire ce projet citoyen, pas seulement pour eux mais aussi avec eux !
              </BlogParagraph>
              <BlogParagraph>
                Nous avons déjà le plaisir d'accueillir les quatre premiers membres de ce Conseil : Ethan, Janan, Lou et Sidonie. Bienvenue à eux ! Si des jeunes de votre entourage souhaitent s'engager à leur tour dans cette belle aventure, n'hésitez pas à nous les recommander !
              </BlogParagraph>
              <BlogParagraph>
                Les détails et modalités pratiques à venir très vite pour le lancement de ce concours "Exprime la Charte".
              </BlogParagraph>
            </>
          }
        />
      </div>

      {/* Third row: fifth post and sixth post */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Fifth blog post */}
        <BlogPost
          id="post5"
          title="Le Portrait du mois : Abdoulaye Kanté"
          imageSrc="/lovable-uploads/411cc424-a0cf-4433-b4e2-ffaa5fe2f274.png"
          imageAlt="Abdoulaye Kanté - Portrait du mois"
          isExpanded={expandedPosts.post5}
          onToggle={togglePost}
          content={
            <>
              <BlogParagraph>
                À 20 ans, Abdoulaye Kanté aurait pu basculer dans l'indifférence ou le ressentiment, comme tant d'autres jeunes des quartiers oubliés. Confronté aux violences urbaines, au racisme ordinaire, il refuse pourtant le cynisme et la résignation, il choisit la République.
              </BlogParagraph>
              <BlogParagraph>
                Ce livre résonne particulièrement avec les valeurs que nous défendons au sein de notre association. Il rappelle l'importance de l'engagement citoyen et du respect des principes républicains. Nous vous invitons à découvrir ce témoignage inspirant, qui illustre le tissu complexe du service public.
              </BlogParagraph>
              <BlogParagraph>
                Dans son ouvrage Policier, enfant de la République, Abdoulaye Kanté, policier à la Direction de la coopération internationale du ministère de l'Intérieur, partage son parcours singulier. Fils d'un pizzaïolo et d'une aide-ménagère, il a grandi entre la banlieue parisienne et le Mali. Son engagement dans la police française est le fruit d'une volonté profonde de servir la République. Dans ce témoignage poignant, il aborde sans détour les défis de son métier, les violences, les dérapages, mais aussi le racisme qu'il a pu rencontrer. Son récit est un plaidoyer pour une police républicaine, juste et au service de la population.
              </BlogParagraph>
              <BlogEmphasis>
                Pourquoi ce portrait ? Parce que notre association croit à ces héros du quotidien, qui incarnent les valeurs que nous voulons défendre.
              </BlogEmphasis>
            </>
          }
        />
        
        {/* Sixth blog post */}
        <BlogPost
          id="post6"
          title="Le dossier : Le Collège Citoyen de France"
          imageSrc="/lovable-uploads/e2124ac2-d856-44a6-9ab8-d00fa62782f1.png"
          imageAlt="Collège Citoyen de France"
          isExpanded={expandedPosts.post6}
          onToggle={togglePost}
          content={
            <>
              <BlogHeading>Le Collège Citoyen de France, une école pour réveiller l'engagement public</BlogHeading>
              <BlogParagraph>
                Le Collège citoyen de France est une initiative lancée en 2021 par un collectif de dix personnalités issues de divers horizons — culture, entreprise, associatif, fonction publique — dont JR, artiste engagé.
              </BlogParagraph>
              <BlogParagraph>
                La philosophie du Collège Citoyen de France rejoint pleinement celle d'Enfants de la République : rendre vivant l'engagement citoyen, restaurer la fierté d'appartenir à la République, et transmettre concrètement les valeurs de liberté, d'égalité, de fraternité et de laïcité. Comme EDLR, il croit que c'est par l'éducation, la formation et l'implication de chacun que notre socle républicain pourra être renforcé et projeté dans l'avenir.
              </BlogParagraph>
              <BlogParagraph>
                Le Collège citoyen de France se présente comme une "école des responsables publics de demain", offrant une formation gratuite et accessible à tous, sans condition de diplôme. Son ambition est de préparer des citoyens issus de la société civile à s'engager dans la vie publique, notamment en vue des élections municipales. Le programme de six mois, compatible avec une activité professionnelle, combine cours théoriques, ateliers pratiques et accompagnement personnalisé en mettant l'accent sur l'éthique, la compétence et l'engagement au service du bien commun.
              </BlogParagraph>
              <BlogParagraph>
                Les cours et ateliers proposés portent sur le fonctionnement des institutions, le droit public, l'organisation territoriale, les finances publiques, mais aussi sur l'éthique de l'action publique. Cette formation est complétée par des exercices pratiques, du compagnonnage et des mises en situation concrètes.
              </BlogParagraph>
              <BlogParagraph>
                Loin de se limiter à un apprentissage théorique, le Collège prône une approche humaine et exigeante de l'engagement : développer des leaders responsables, animés par l'intérêt général, fidèles aux valeurs de la République et attentifs aux réalités du terrain.
              </BlogParagraph>
              <BlogParagraph>
                Le succès de cette école citoyenne est visible : plusieurs de ses anciens élèves se sont déjà engagés dans la vie publique locale, que ce soit en se présentant aux élections ou en lançant des projets d'intérêt général dans leur commune.
              </BlogParagraph>
              <BlogParagraph>
                Le Collège Citoyen de France participe ainsi à un renouveau démocratique, fondé sur la compétence, l'éthique et la proximité avec les citoyens.
              </BlogParagraph>
              <BlogEmphasis>
                La République n'a pas seulement besoin d'être défendue. Elle doit être vécue.
              </BlogEmphasis>
            </>
          }
        />
      </div>

      {/* Fourth row: seventh post and ninth post (Concours Exprime la Charte) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Ninth blog post - Concours Exprime la Charte */}
        <BlogPost
          id="post9"
          title="Concours « Exprime la Charte ! » - Académie d'Aix-Marseille"
          imageSrc="/lovable-uploads/concours-jeunes-table.png"
          imageAlt="Concours Exprime la Charte"
          isExpanded={expandedPosts.post9}
          onToggle={togglePost}
          content={
            <>
              <BlogHeading>L'Académie d'Aix-Marseille lance le concours « Exprime la Charte » !</BlogHeading>
              <BlogParagraph>
                Le concours « Exprime la Charte ! » invite les élèves à s'approprier les valeurs républicaines en réalisant une production collective (texte, affiche, vidéo, slam, dessin...) autour de la Charte des Droits et Devoirs du Citoyen français.
              </BlogParagraph>
              <BlogParagraph>
                L'association a proposé à l'Académie d'Aix-Marseille l'organisation d'un concours, intitulé « Exprime la Charte ! », qui a pour objectif de permettre aux jeunes d'exprimer cette charte à travers leurs propres codes : en mots, en images et/ou en musique.
              </BlogParagraph>
              <BlogParagraph>
                Il s'agit d'un travail d'équipe, où les participants expriment tout ou partie de la Charte en lien avec les valeurs fondamentales de la République : Liberté, Égalité, Fraternité, Laïcité.
              </BlogParagraph>

              <BlogHeading>Objectifs</BlogHeading>
              <BlogParagraph>
                Ce concours vise ainsi à :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">➢ encourager la compréhension active et l'appropriation du « Vivre Ensemble » et de la citoyenneté, autour des valeurs de la République : Liberté, Égalité, Fraternité, Laïcité ;</li>
                <li className="text-gray-700">➢ renforcer le dialogue et le débat respectueux au sein d'un groupe (ou d'une classe), appuyé par un module pédagogique à l'adresse des professeurs et des élèves ;</li>
                <li className="text-gray-700">➢ dynamiser la création collective au sein des classes ;</li>
                <li className="text-gray-700">➢ créer une dynamique positive et des passerelles entre l'École, la Culture et la Citoyenneté ;</li>
                <li className="text-gray-700">➢ faire des productions lauréates des outils pour sensibiliser et mobiliser d'autres jeunes, pour démultiplier l'adhésion à la Charte par les jeunes générations.</li>
              </ul>

              <BlogHeading>Cible</BlogHeading>
              <BlogParagraph>
                Pour l'année scolaire 2025-2026, le concours est ouvert à toutes les classes de la 4e à la 2ᵈᵉ (élèves de 13 à 16 ans) de l'Académie d'Aix-Marseille.
              </BlogParagraph>

              <BlogHeading>Règlement du concours</BlogHeading>
              <div className="my-6 flex justify-center">
                <img 
                  src="/lovable-uploads/concours-reglement-schema.png" 
                  alt="Règlement du concours Exprime la Charte" 
                  className="w-3/4 rounded-lg shadow-md"
                />
              </div>

              <BlogHeading>Modalités pratiques de participation</BlogHeading>
              <BlogParagraph>
                Les productions des élèves devront se faire par groupe (5 élèves minimum) encadré par un référent adulte. Les réalisations peuvent être numériques ou physiques.
              </BlogParagraph>

              <BlogHeading>Conditions de participation</BlogHeading>
              <BlogParagraph>
                Chaque classe ou groupe qui souhaite participer doit transmettre :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">● Un formulaire d'inscription au concours ;</li>
                <li className="text-gray-700">● La production finale (une seule production par classe ou par groupe inscrit) ;</li>
                <li className="text-gray-700">● Un formulaire de présentation du projet (incluant le titre, les participants, une brève description, et la catégorie choisie) ;</li>
                <li className="text-gray-700">● Une attestation de droit à l'image si des visages ou des voix sont reconnaissables ;</li>
                <li className="text-gray-700">● Un avis du professeur référent ou chef d'établissement (facultatif, mais recommandé).</li>
              </ul>

              <BlogHeading>Format et expression</BlogHeading>
              <BlogParagraph>
                Les productions doivent relever d'une des trois catégories ci-dessous. Il est tout à fait possible de présenter une vidéo incluant de l'audio, qui sera classifiée dans la catégorie « image » ou dans la catégorie « son » selon le choix des participants.
              </BlogParagraph>
              <BlogParagraph>
                Les trois catégories sont :
              </BlogParagraph>

              <BlogParagraph>
                <strong>Mots</strong>
              </BlogParagraph>
              <BlogParagraph>
                Différents types de production sont possibles : texte, nouvelle, discours, slam, poésie… Quel que soit le type de production, elle devra respecter les consignes suivantes : un maximum de 3 pages et/ou 1500 mots, sous forme de police lisible, taille 11 ou 12, format PDF ou Word.
              </BlogParagraph>

              <BlogParagraph>
                <strong>Images</strong>
              </BlogParagraph>
              <BlogParagraph>
                Différents types de production sont possibles :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700"><strong>Affiche, dessin, photo</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : un visuel unique, au format A4 ou A3, en orientation libre.</li>
                <li className="text-gray-700"><strong>Bande dessinée</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : 2 planches maximum pour un total de 12 cases maximum.</li>
                <li className="text-gray-700"><strong>Vidéo</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : durée maximale de 2 minutes, au format MP4.</li>
              </ul>

              <BlogParagraph>
                <strong>Son</strong>
              </BlogParagraph>
              <BlogParagraph>
                Différents types de production sont possibles :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700"><strong>Chanson</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : durée maximale de 3 minutes, format MP3 ou WAV, taille maximale de 10 Mo.</li>
                <li className="text-gray-700"><strong>Podcast</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : durée maximale de 4 minutes, format MP3 ou WAV, taille maximale de 10 Mo.</li>
                <li className="text-gray-700"><strong>Vidéo</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : durée maximale de 2 minutes, au format MP4.</li>
                <li className="text-gray-700"><strong>Autre création sonore</strong><br />
                Dans ce cas, la production devra respecter les consignes suivantes : format MP3 ou WAV, taille maximale de 10 Mo.</li>
              </ul>

              <BlogHeading>Accompagnement pédagogique</BlogHeading>
              <BlogParagraph>
                Un module pédagogique, destiné aux enseignants et personnels éducatifs qui encadreront les groupes d'élèves souhaitant participer, est mis à disposition dans ce livret afin de faciliter la mise en œuvre du projet, favoriser l'échange entre élèves et garantir une articulation avec les objectifs des programmes d'Enseignement Moral et Civique notamment. Il permet d'aborder les valeurs clés de la Charte avec les élèves, en amont de leur participation au concours, à travers une séquence pédagogique complète.
              </BlogParagraph>
              <BlogParagraph>
                Un contenu explicatif de la Charte des Droits et Devoirs du Citoyen français, au format numérique, adapté aux élèves, est par ailleurs mis en ligne sur :
              </BlogParagraph>

              <BlogHeading>Calendrier du concours</BlogHeading>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">● Lancement du concours : 5 novembre 2025</li>
                <li className="text-gray-700">● Clôture des inscriptions : 20 décembre 2025</li>
                <li className="text-gray-700">● Remise des travaux : 27 mai 2026</li>
                <li className="text-gray-700">● Publication du palmarès par catégorie : 5 juin 2026</li>
              </ul>
              <BlogParagraph>
                Les productions doivent être envoyées à l'adresse suivante (mail ou plateforme dédiée précisée à l'inscription) au plus tard le 27 mai 2026.
              </BlogParagraph>

              <BlogHeading>Composition du jury</BlogHeading>
              <BlogParagraph>
                Le jury du concours se compose :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">● des membres du Conseil des Jeunes Sages de l'association Enfants de la République (voir ci-dessous) ;</li>
                <li className="text-gray-700">● des membres du comité de direction de l'association Enfants de la République ;</li>
                <li className="text-gray-700">● d'un expert de chaque catégorie (écrivain, artiste visuel, compositeur…) ;</li>
                <li className="text-gray-700">● d'un représentant de l'Éducation nationale : référent Mémoire et Citoyenneté, de l'Académie d'Aix-Marseille.</li>
              </ul>

              <BlogParagraph>
                <strong>Le Conseil des Jeunes Sages</strong>
              </BlogParagraph>
              <BlogParagraph>
                Parce que l'association veut s'assurer que le concours suscite l'intérêt des collégiens et lycéens d'une part, et que le choix des lauréats s'effectue selon les codes de la génération ciblée d'autre part, un Conseil des Jeunes Sages a été constitué. Son rôle est d'accompagner l'association de la création du concours jusqu'au choix des lauréats. C'est une instance consultative de l'association Enfants de la République.
              </BlogParagraph>
              <BlogParagraph>
                Il s'agit d'une équipe de 6 jeunes de 13 à 16 ans, d'origines géographiques et culturelles variées. Ils sont accompagnés dans leurs tâches par deux membres de l'association, dont une psychologue. Les jeunes sont volontaires et se sont engagés pour une durée minimale d'un an. Ils participent à une réunion par mois.
              </BlogParagraph>

              <BlogHeading>Les critères clés d'évaluation</BlogHeading>
              <BlogParagraph>
                Le jury tiendra compte des critères suivants :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">● Compréhension et expression des valeurs de la République</li>
                <li className="text-gray-700">● Qualité créative et originalité</li>
                <li className="text-gray-700">● Portée collective de l'engagement citoyen</li>
                <li className="text-gray-700">● Clarté et lisibilité de la production</li>
              </ul>
              <BlogParagraph>
                La grille d'évaluation qui sera utilisée pour le concours est à retrouver en annexe (page 76). Afin de permettre aux élèves de préparer leur production pour le concours de manière éclairée, vous pouvez leur communiquer cette grille d'évaluation dès la première séance.
              </BlogParagraph>

              <BlogHeading>Remise des prix et valorisation</BlogHeading>
              <BlogParagraph>
                Les projets lauréats seront :
              </BlogParagraph>
              <ul className="list-none space-y-2 ml-4 mb-4">
                <li className="text-gray-700">● Présentés lors d'un événement régional organisé en partenariat avec l'Académie d'Aix-Marseille ;</li>
                <li className="text-gray-700">● Diffusés sur les supports de l'association Enfants de la République (site web, réseaux sociaux, expositions itinérantes…).</li>
              </ul>
              <BlogParagraph>
                Des mentions spéciales pourront être attribuées (créativité, message citoyen, engagement collectif…).
              </BlogParagraph>

              <BlogHeading>Droits et consentement</BlogHeading>
              <BlogParagraph>
                La participation au concours implique l'acceptation du règlement. Les participants (et leurs représentants légaux le cas échéant) autorisent l'utilisation non commerciale de leurs productions dans les actions de communication de l'association Enfants de la République et de ses partenaires, dans le respect du droit à l'image et du droit d'auteur.
              </BlogParagraph>

              <BlogHeading>Démarche pédagogique du module d'accompagnement</BlogHeading>
              <BlogParagraph>
                L'association Enfants de la République souhaite promouvoir la Charte des Droits et Devoirs du Citoyen français à travers le concours « Exprime la Charte ! »
              </BlogParagraph>
              <BlogParagraph>
                Afin d'accompagner les enseignants dans la mise en oeuvre pratique du concours, ce livret pédagogique propose une séquence pédagogique complète permettant d'aborder avec les élèves les thématiques principales associées à la Charte. Cette organisation spécifique en séquence n'est qu'une suggestion faite aux enseignants, qui gardent toute liberté pédagogique lors de la préparation du concours. Il n'est donc pas obligatoire ni nécessaire de réaliser cette séquence spécifique pour pouvoir participer au concours. Par ailleurs, chacune des séances proposées dans ce livret pédagogique peut être réalisée séparément et indépendamment des autres.
              </BlogParagraph>
              <BlogParagraph>
                La démarche pédagogique proposée dans cette séquence repose sur trois grands principes : l'expérience vécue, l'expression partagée et l'appropriation active. L'enjeu n'est pas seulement de transmettre un texte, mais de permettre aux élèves de se l'approprier par la réflexion, le débat et la créativité.
              </BlogParagraph>

              <BlogHeading>Progression en 5 séances</BlogHeading>
              <BlogParagraph>
                La séquence se déploie en cinq séances de 45 minutes environ, accompagnées de proposition d'adaptation selon le niveau des élèves :
              </BlogParagraph>
              <ul className="list-disc pl-6 space-y-2 text-foreground">
                <li><strong>1. Liberté & respect :</strong> peut-on tout dire ?</li>
                <li><strong>2. Égalité :</strong> est-ce toujours juste ?</li>
                <li><strong>3. Fraternité, solidarité, laïcité :</strong> Comment mieux vivre ensemble ?</li>
                <li><strong>4. Expression citoyenne :</strong> Comment faire entendre ma voix ?</li>
                <li><strong>5. Citoyenneté en action :</strong> Comment agir et transmettre les valeurs de la République ? → Réalisation concrète d'une production, dans le cadre du concours</li>
              </ul>
              <BlogParagraph>
                (+ séances facultatives pour parfaire la production)
              </BlogParagraph>

              <BlogHeading>Exemples de cartes pour la séance 1 : Liberté & Respect : Peut-on tout dire ?</BlogHeading>
              <img 
                src="/lovable-uploads/concours-exemples-cartes.png" 
                alt="Exemples de cartes pour la séance sur la liberté et le respect"
                className="w-1/2 rounded-lg my-6 mx-auto"
              />

              <BlogHeading>Contact et inscription</BlogHeading>
              <BlogParagraph>
                Pour toute question ou inscription :<br />
                📧 concours@ledlr.org
              </BlogParagraph>

              <BlogEmphasis>
                Rejoignez-nous pour cette belle aventure citoyenne et créative !
              </BlogEmphasis>
            </>
          }
         />
      </div>

      {/* Fifth row: seventh post and quiz post */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Seventh blog post */}
        <BlogPost
          id="post7"
          title="L'actualité nationale"
          imageSrc="/lovable-uploads/adb0e545-9d89-432c-bec4-fd2436970307.png"
          imageAlt="Journée de nettoyage citoyen - Clean walk"
          isExpanded={expandedPosts.post7}
          onToggle={togglePost}
          content={
            <>
              <BlogHeading>1. Journées de nettoyage citoyen (« clean walk »)</BlogHeading>
              <BlogParagraph>
                De nombreuses villes organisent des opérations de ramassage des déchets, souvent dans le cadre de la Semaine de la réduction des déchets. Ces initiatives mobilisent des milliers de citoyens, associations et écoles pour sensibiliser à l'environnement et lutter contre les déchets sauvages. Le mouvement des « clean walk » gagne en visibilité grâce à l'engagement de jeunes et d'influenceurs, et chacun peut rejoindre ou initier une action locale via des plateformes dédiées.
              </BlogParagraph>
              
              <BlogHeading>2. Le désenchantement démocratique des jeunes</BlogHeading>
              <BlogParagraph>
                En avril 2025, un rapport de l'Institut Montaigne alerte : près de 48 % des jeunes de moins de 25 ans considèrent la démocratie "imparfaite" ou "non représentative", et plus de 30 % estiment que "d'autres systèmes pourraient être préférables".
              </BlogParagraph>
              <BlogParagraph>
                Dans le même temps, l'abstention aux élections européennes de juin 2024 a atteint un record historique : 59 % chez les 18-35 ans. Pourquoi ce désenchantement ?
              </BlogParagraph>
              <BlogParagraph>
                Parce que la citoyenneté est devenue un concept abstrait, déconnecté des réalités vécues : crise économique, défiance envers les élites, sentiment d'injustice culturelle et sociale.
              </BlogParagraph>
            </>
          }
        />
        
        {/* New Quiz blog post */}
        <BlogPost
          id="post8"
          title="QUIZ : Connaissez-vous la Charte des droits et des devoirs du citoyen français ?"
          imageSrc="/lovable-uploads/96790f9b-13f3-4f10-98f7-c9242a5bb41a.png"
          imageAlt="Quiz sur la Charte - Illustration colorée"
          isExpanded={expandedPosts.post8 || false}
          onToggle={togglePost}
          content={
            <>
              <BlogQuizQuestion
                question="❶ La Charte des droits et des devoirs du citoyen français est :"
                options={[
                  "A. Un document facultatif remis lors de l'obtention de la carte d'identité",
                  "B. Un document officiel que tout futur Français doit signer lors d'une demande de naturalisation",
                  "C. Une déclaration politique votée tous les cinq ans"
                ]}
                answer="B"
                explanation="Elle est annexée à toute demande de naturalisation (art. 21-24 du Code civil). Elle rappelle les principes fondamentaux de la République et les engagements citoyens."
              />
              
              <BlogQuizQuestion
                question="❷ Combien d'articles contient la Charte des droits et des devoirs du citoyen français ?"
                options={[
                  "A. 5",
                  "B. 7",
                  "C. 10"
                ]}
                answer="C"
                explanation="Elle contient 10 articles, chacun évoquant un droit fondamental et le devoir correspondant."
              />
              
              <BlogQuizQuestion
                question="❸ Lequel de ces devoirs figure dans la Charte ?"
                options={[
                  "A. Connaître l'hymne national",
                  "B. Participer à la vie démocratique, notamment par le vote",
                  "C. Porter un insigne tricolore lors des élections"
                ]}
                answer="B"
                explanation="Le devoir de participer à la vie démocratique est explicitement mentionné comme un fondement de la citoyenneté."
              />
              
              <BlogQuizQuestion
                question="❹ Cette Charte s'appuie principalement sur :"
                options={[
                  "A. La Déclaration des Droits de l'Homme et du Citoyen de 1789",
                  "B. La Constitution américaine",
                  "C. Le Code du travail français"
                ]}
                answer="A"
                explanation="Elle s'inspire du bloc de constitutionnalité, dont la Déclaration de 1789 fait partie intégrante, ainsi que de la Constitution de 1958 et des lois républicaines."
              />
              
              <BlogQuizQuestion
                question="❺ Peut-on proposer que cette Charte soit signée par tous les Français, et non seulement les nouveaux ?"
                options={[
                  "A. Non, ce serait inconstitutionnel",
                  "B. Oui, c'est une proposition citoyenne et politique défendue par certaines associations",
                  "C. Oui, c'est déjà le cas pour tous les élèves français"
                ]}
                answer="B"
                explanation="L'association Enfants de la République propose justement d'en faire un pacte citoyen universel : un engagement explicite pour tous les Français, dès l'école et tout au long de la vie."
              />
            </>
          }
        />
      </div>
    </div>
  );
};
